/**
 * Serviço para gerenciar agentes de IA
 */

import { getToken } from "./tokenService";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface PromptStep {
  id?: string;
  agent_version_id?: string;
  name: string;
  prompt: string;
  order_num: number;
  created_at?: string;
  updated_at?: string;
}

export interface AgentVersion {
  id: string;
  agent_id: string;
  version: string;
  description: string;
  is_published: boolean;
  is_active: boolean;
  created_at: string;
  published_at?: string;
  steps: PromptStep[];
}

export interface Agent {
  id: string;
  name: string;
  service: string;
  description: string;
  type_id: number;
  type_name?: string;
  active_version_id?: string;
  active_version?: string;
  active_version_description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  versions?: AgentVersion[];
}

export interface AgentsListResponse {
  agents: Agent[];
  total: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    pages: number;
  };
}

export interface CreateAgentRequest {
  name: string;
  service: string;
  description: string;
  type_id: number;
  is_active?: boolean;
}

export interface UpdateAgentRequest {
  name?: string;
  service?: string;
  description?: string;
  type_id?: number;
  is_active?: boolean;
}

export interface CreateVersionRequest {
  description: string;
  is_published?: boolean;
  steps: PromptStep[];
  change_type?: "initial" | "content_change" | "steps_change" | "unpublish";
}

export interface UpdateVersionRequest {
  description?: string;
  is_published?: boolean;
  steps?: PromptStep[];
  change_type?: "initial" | "content_change" | "steps_change" | "unpublish";
}

export interface AgentsStats {
  total_agents: number;
  active_agents: number;
  agents_by_type: Array<{ type_name: string; count: string }>;
  total_versions: number;
}

export interface AgentType {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista agentes com filtros e paginação
 */
export async function listAgents(options?: {
  limit?: number;
  offset?: number;
  type_id?: number;
  is_active?: boolean;
  search?: string;
}): Promise<ApiResponse<AgentsListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.type_id) params.append("type_id", options.type_id.toString());
  if (options?.is_active !== undefined)
    params.append("is_active", options.is_active.toString());
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  return apiRequest<AgentsListResponse>(
    `/agents${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Obtém um agente por ID
 */
export async function getAgentById(id: string): Promise<ApiResponse<Agent>> {
  return apiRequest<Agent>(`/agents/${id}`);
}

/**
 * Cria um novo agente
 */
export async function createAgent(
  agent: CreateAgentRequest
): Promise<ApiResponse<Agent>> {
  return apiRequest<Agent>("/agents", "POST", agent);
}

/**
 * Atualiza um agente
 */
export async function updateAgent(
  id: string,
  agent: UpdateAgentRequest
): Promise<ApiResponse<Agent>> {
  return apiRequest<Agent>(`/agents/${id}`, "PUT", agent);
}

/**
 * Remove um agente
 */
export async function deleteAgent(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/agents/${id}`, "DELETE");
}

/**
 * Ativa ou desativa um agente
 */
export async function toggleAgentStatus(
  id: string,
  isActive: boolean
): Promise<ApiResponse<Agent>> {
  return apiRequest<Agent>(`/agents/${id}/toggle-status`, "PATCH", {
    is_active: isActive,
  });
}

/**
 * Obtém estatísticas dos agentes
 */
export async function getAgentsStats(): Promise<ApiResponse<AgentsStats>> {
  return apiRequest<AgentsStats>("/agents/stats");
}

/**
 * Lista tipos de agentes
 */
export async function listAgentTypes(): Promise<ApiResponse<AgentType[]>> {
  return apiRequest<AgentType[]>("/types/agent-types");
}

// ========== VERSÕES ==========

/**
 * Lista versões de um agente
 */
export async function listAgentVersions(
  agentId: string
): Promise<ApiResponse<AgentVersion[]>> {
  return apiRequest<AgentVersion[]>(`/agents/${agentId}/versions`);
}

/**
 * Cria uma nova versão de agente
 */
export async function createAgentVersion(
  agentId: string,
  version: CreateVersionRequest
): Promise<ApiResponse<AgentVersion>> {
  return apiRequest<AgentVersion>(
    `/agents/${agentId}/versions`,
    "POST",
    version
  );
}

/**
 * Obtém uma versão específica
 */
export async function getAgentVersionById(
  versionId: string
): Promise<ApiResponse<AgentVersion>> {
  return apiRequest<AgentVersion>(`/agents/versions/${versionId}`);
}

/**
 * Atualiza uma versão de agente
 */
export async function updateAgentVersion(
  versionId: string,
  version: UpdateVersionRequest
): Promise<ApiResponse<AgentVersion>> {
  return apiRequest<AgentVersion>(
    `/agents/versions/${versionId}`,
    "PUT",
    version
  );
}

/**
 * Remove uma versão de agente
 */
export async function deleteAgentVersion(
  versionId: string
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/agents/versions/${versionId}`, "DELETE");
}

/**
 * Publica uma versão (ativa e marca como publicada)
 */
export async function publishAgentVersion(
  versionId: string
): Promise<ApiResponse<AgentVersion>> {
  return apiRequest<AgentVersion>(
    `/agents/versions/${versionId}/publish`,
    "POST"
  );
}

/**
 * Despublica uma versão e cria nova versão major
 */
export async function unpublishAgentVersion(
  versionId: string
): Promise<ApiResponse<AgentVersion>> {
  return apiRequest<AgentVersion>(
    `/agents/versions/${versionId}/unpublish`,
    "POST"
  );
}

/**
 * Verifica se um serviço já existe
 */
export async function checkServiceExists(
  service: string,
  excludeAgentId?: string
): Promise<ApiResponse<{ exists: boolean }>> {
  const response = await listAgents({ search: service });

  if (response.success && response.data) {
    const agents = response.data.agents;
    const exists = agents.some(
      (agent) => agent.service === service && agent.id !== excludeAgentId
    );
    return {
      success: true,
      data: { exists },
    };
  }

  return response as ApiResponse<{ exists: boolean }>;
}
